import { effect, inject, Injectable, signal } from '@angular/core';

import {
  TellentAssistantActionsService,
  TellentAssistantAnalyticsService,
  TellentAssistantService,
} from '@core/tellent-assistant-logic';
import type { ChatPart } from '@core/tellent-assistant-types';
import type { ProposalDecisionEvent } from '@core/tellent-assistant-ui';

type ProposeActionPart = Extract<ChatPart, { kind: 'propose-action' }>;

/** The proposal's args as the params record the execute endpoint expects. */
const argsRecord = (part: ProposeActionPart): Record<string, unknown> =>
  typeof part.args === 'object' && part.args !== null ? <Record<string, unknown>>part.args : {};

const without = (ids: ReadonlySet<string>, id: string): ReadonlySet<string> => {
  const next = new Set(ids);
  next.delete(id);
  return next;
};

@Injectable()
export class ChatPanelProposalsService {
  private readonly assistantService = inject(TellentAssistantService);
  private readonly actionsService = inject(TellentAssistantActionsService);
  private readonly analytics = inject(TellentAssistantAnalyticsService);

  // Decisions happen inline on each proposal card; these track per-proposal
  // dispatch state so the card can disable its buttons / show an error row.
  private readonly busyProposals = signal<ReadonlySet<string>>(new Set());
  private readonly failedProposals = signal<ReadonlySet<string>>(new Set());

  protected readonly loadResolutionsOnThread = effect(() => {
    const threadId = this.assistantService.currentThreadId();
    if (!threadId) return;
    // Only fetch when the thread actually contains proposals (most don't);
    // the service dedups per thread, so streaming updates don't re-fetch.
    // Load failure is cosmetic (cards render idle); backend idempotency
    // prevents a stale-looking card from double-executing.
    const hasProposals = this.assistantService
      .messages()
      .some(message => message.parts.some(part => part.kind === 'propose-action'));
    if (hasProposals) this.actionsService.loadForThread(threadId).catch(() => undefined);
  });

  public readonly actionResolver = (proposalId: string) =>
    this.actionsService.resolutionOf(proposalId);

  public readonly resultLinkResolver = (proposalId: string) =>
    this.actionsService.resultLinkOf(proposalId);

  public readonly decisionBusyResolver = (proposalId: string): boolean =>
    this.busyProposals().has(proposalId);

  public readonly decisionErrorResolver = (proposalId: string): boolean =>
    this.failedProposals().has(proposalId);

  public async decide({ part, decision }: ProposalDecisionEvent): Promise<void> {
    const threadId = this.assistantService.currentThreadId();
    if (!threadId) return;

    if (decision === 'skip') {
      this.analytics.proposalSkipped(part.action);
      this.actionsService.markCancelled(threadId, part.proposalId, part.action);
      return;
    }

    this.analytics.proposalConfirmed(part.action);
    this.busyProposals.update(ids => new Set(ids).add(part.proposalId));
    this.failedProposals.update(ids => without(ids, part.proposalId));
    try {
      await this.actionsService.execute(threadId, part.proposalId, part.action, argsRecord(part));
    } catch {
      this.failedProposals.update(ids => new Set(ids).add(part.proposalId));
    } finally {
      this.busyProposals.update(ids => without(ids, part.proposalId));
    }
  }
}
